import { ResponsiveSidebar } from "@/components/ResponsiveSidebar";
import React, { Suspense, lazy, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { PitechPageLoader } from "@/components/PitechLoader";
import { QuoteModalProvider } from "@/components/QuoteModal";
import { CalendlyProvider } from "@/components/CalendlyPopup";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { PlatformProvider } from "@/contexts/PlatformContext";

// Lazy load pages with auto-retry + timeout guard to avoid endless white loading screens
const CHUNK_LOAD_TIMEOUT_MS = 12000;

const withChunkTimeout = (importPromise: Promise<any>, timeoutMs = CHUNK_LOAD_TIMEOUT_MS) =>
  Promise.race([
    importPromise,
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`Chunk load timeout after ${timeoutMs}ms`)), timeoutMs);
    }),
  ]);

const lazyWithRetry = (importFn: () => Promise<any>) =>
  lazy(async () => {
    try {
      return await withChunkTimeout(importFn());
    } catch (error) {
      // Reload once on stale/hung chunk, then surface error instead of infinite loading
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', 'true');
        window.location.reload();
      }
      throw error;
    }
  });

const lazyWithDelay = lazyWithRetry;

// Lazy load all pages with smooth loading transitions
const Index = lazyWithDelay(() => import("./pages/Index"));
const Auth = lazyWithDelay(() => import("./pages/Auth"));
const Dashboard = lazyWithDelay(() => import("./pages/Dashboard"));
const QRCodes = lazyWithDelay(() => import("./pages/QRCodes"));
const Analytics = lazyWithDelay(() => import("./pages/Analytics"));
const AnalyticsDetailed = lazyWithDelay(() => import("./pages/AnalyticsDetailed"));
const Settings = lazyWithDelay(() => import("./pages/Settings"));
const AdminDashboardComplete = lazyWithDelay(() => import("./pages/AdminDashboardComplete"));
const Invoices = lazyWithDelay(() => import("./pages/Invoices"));
const Notifications = lazyWithDelay(() => import("./pages/Notifications"));
const MenuBuilder = lazyWithDelay(() => import("./pages/MenuBuilder"));
const CreateMenu = lazyWithDelay(() => import("./pages/CreateMenu"));
const EditMenuComplete = lazyWithDelay(() => import("./pages/EditMenuComplete"));
const EditCategory = lazyWithDelay(() => import("./pages/EditCategory"));
const MenuBuilderComplete = lazyWithDelay(() => import("./pages/MenuBuilderComplete"));
const QRDesigner = lazyWithDelay(() => import("./pages/QRDesigner"));
const PublicMenu = lazyWithDelay(() => import("./pages/PublicMenu"));
const PublicBlog = lazyWithDelay(() => import("./pages/PublicBlog"));
const PublicReservation = lazyWithDelay(() => import("./pages/PublicReservation"));
const PublicAppointment = lazyWithDelay(() => import("./pages/PublicAppointment"));
const BlogList = lazyWithDelay(() => import("./pages/BlogList"));
const BlogTagPage = lazyWithDelay(() => import("./pages/BlogTagPage"));
const BlogCategoryPage = lazyWithDelay(() => import("./pages/BlogCategoryPage"));
const AffiliatePortal = lazyWithDelay(() => import("./pages/AffiliatePortal"));
const AffiliateSettings = lazyWithDelay(() => import("./pages/AffiliateSettings"));
const MarketingCampaigns = lazyWithDelay(() => import("./pages/MarketingCampaigns"));
const CreateCampaign = lazyWithDelay(() => import("./pages/CreateCampaign"));
const GoogleMarketing = lazyWithDelay(() => import("./components/marketing/GoogleMarketingView"));
const SessionReplays = lazyWithDelay(() => import("./pages/SessionReplays"));
const MLMPrograms = lazyWithDelay(() => import("./pages/MLMPrograms"));
const JoinProgram = lazyWithDelay(() => import("./pages/JoinProgram"));
const PartnerPortal = lazyWithDelay(() => import("./pages/PartnerPortal"));
const TestPayment = lazyWithDelay(() => import("./pages/TestPayment"));
const BlogManagement = lazyWithDelay(() => import("./pages/BlogManagement"));
const CreateBlogComplete = lazyWithDelay(() => import("./pages/CreateBlogComplete"));
const EditBlog = lazyWithDelay(() => import("./pages/EditBlog"));
const ContentStrategyPage = lazyWithDelay(() => import("./pages/ContentStrategyPage"));
const BlogPostAnalytics = lazyWithDelay(() => import("./pages/BlogPostAnalytics"));
const RoleManagement = lazyWithDelay(() => import("./pages/RoleManagement"));
const FileManager = lazyWithDelay(() => import("./pages/FileManager"));
const SiteConfiguration = lazyWithDelay(() => import("./pages/SiteConfiguration"));
const AdminPageSeo = lazyWithDelay(() => import("./pages/AdminPageSeo"));
const SubscriptionExpired = lazyWithDelay(() => import("./pages/SubscriptionExpired"));
const ShortLink = lazyWithDelay(() => import("./pages/ShortLink"));
const PageManagement = lazyWithDelay(() => import("./pages/PageManagement"));
const AdminBlogImport = lazyWithDelay(() => import("./pages/AdminBlogImport"));
const AdminNewsletter = lazyWithDelay(() => import("./pages/AdminNewsletter"));
const TenantManagement = lazyWithDelay(() => import("./pages/TenantManagement"));
const SubscriptionPlansAdmin = lazyWithDelay(() => import("./pages/SubscriptionPlansAdmin"));
const SaaSFeaturesDashboard = lazyWithDelay(() => import("./pages/SaaSFeaturesDashboard"));
const AIEditorsAdmin = lazyWithDelay(() => import("./pages/AIEditorsAdmin"));
const UserActivityTracking = lazyWithDelay(() => import("./pages/UserActivityTracking"));
const Security = lazyWithDelay(() => import("./pages/Security"));
const APIGatewayDashboard = lazyWithDelay(() => import("./pages/APIGatewayDashboard"));
const NotFound = lazyWithDelay(() => import("./pages/NotFound"));
const UserWallet = lazyWithDelay(() => import("./pages/UserWallet"));
const RewardsPrograms = lazyWithDelay(() => import("./pages/RewardsPrograms"));
const WalletPayouts = lazyWithDelay(() => import("./pages/WalletPayouts"));
const WalletAdmin = lazyWithDelay(() => import("./pages/WalletAdmin"));
const GiftCardActivation = lazyWithDelay(() => import("./pages/GiftCardActivation"));
const GiftCards = lazyWithDelay(() => import("./pages/GiftCards"));
const GiftCardAdmin = lazyWithDelay(() => import("./pages/GiftCardAdmin"));
const AdminUserManagement = lazyWithDelay(() => import("./pages/AdminUserManagement"));
const Store = lazyWithDelay(() => import("./pages/Store"));
const Cart = lazyWithDelay(() => import("./pages/Cart"));
const Checkout = lazyWithDelay(() => import("./pages/Checkout"));
const AdminProducts = lazyWithDelay(() => import("./pages/AdminProducts"));
const AdminOrders = lazyWithDelay(() => import("./pages/AdminOrders"));
const AdminAppointments = lazyWithDelay(() => import("./pages/AdminAppointments"));
const AdminReservations = lazyWithDelay(() => import("./pages/AdminReservations"));
const AdminFloorPlan = lazyWithDelay(() => import("./pages/AdminFloorPlan"));
const PageBuilder = lazyWithDelay(() => import("./pages/PageBuilderNew"));
const PublicPage = lazyWithDelay(() => import("./pages/PublicPage"));
const EnhancedAuth = lazyWithDelay(() => import("./pages/EnhancedAuth"));
const SimpleWordPressImport = lazyWithDelay(() => import("./pages/SimpleWordPressImport"));
const CreateQR = lazyWithDelay(() => import("./pages/CreateQR"));
const Pricing = lazyWithDelay(() => import("./pages/Pricing"));
const ProductDetail = lazyWithDelay(() => import("./pages/ProductDetail"));
const APISettings = lazyWithDelay(() => import("./pages/APISettings"));
const CompleteWallet = lazyWithDelay(() => import("./pages/CompleteWallet"));
const FormBuilder = lazyWithDelay(() => import("./pages/FormBuilder"));
const AdminCategories = lazyWithDelay(() => import("./pages/AdminCategories"));
const Services = lazyWithDelay(() => import("./pages/Services"));
const ServicesCanada = lazyWithDelay(() => import("./pages/ServicesCanada"));
const ServicesNearMe = lazyWithDelay(() => import("./pages/ServicesNearMe"));
const ProvincePage = lazyWithDelay(() => import("./pages/ProvincePage"));
const CityPage = lazyWithDelay(() => import("./pages/CityPage"));
const About = lazyWithDelay(() => import("./pages/About"));
const Contact = lazyWithDelay(() => import("./pages/Contact"));
const GetAQuote = lazyWithDelay(() => import("./pages/GetAQuote"));
const QuoteThankYou = lazyWithDelay(() => import("./pages/QuoteThankYou"));
const CustomSoftware = lazyWithDelay(() => import("./pages/services/CustomSoftware"));
const MobileAppDevelopment = lazyWithDelay(() => import("./pages/services/MobileAppDevelopment"));
const WebAppDevelopment = lazyWithDelay(() => import("./pages/services/WebAppDevelopment"));
const CloudSolutions = lazyWithDelay(() => import("./pages/services/CloudSolutions"));
const EnterpriseSoftware = lazyWithDelay(() => import("./pages/services/EnterpriseSoftware"));
const SoftwareConsulting = lazyWithDelay(() => import("./pages/services/SoftwareConsulting"));
const DomainServices = lazyWithDelay(() => import("./pages/services/DomainServices"));
const SEO = lazyWithDelay(() => import("./pages/services/SEO"));
const PPCAdvertising = lazyWithDelay(() => import("./pages/services/PPCAdvertising"));
const EmailMarketing = lazyWithDelay(() => import("./pages/services/EmailMarketing"));
const SocialMediaMarketing = lazyWithDelay(() => import("./pages/services/SocialMediaMarketing"));
const ContentMarketing = lazyWithDelay(() => import("./pages/services/ContentMarketing"));
const AnalyticsReporting = lazyWithDelay(() => import("./pages/services/AnalyticsReporting"));
const DigitalMarketing = lazyWithDelay(() => import("./pages/services/DigitalMarketing"));
const CreativeStudio = lazyWithDelay(() => import("./pages/services/CreativeStudio"));
const VulnerabilityAssessment = lazyWithDelay(() => import("./pages/services/VulnerabilityAssessment"));
const SecurityConsulting = lazyWithDelay(() => import("./pages/services/SecurityConsulting"));
const DataProtection = lazyWithDelay(() => import("./pages/services/DataProtection"));
const NetworkSecurity = lazyWithDelay(() => import("./pages/services/NetworkSecurity"));
const ThreatDetection = lazyWithDelay(() => import("./pages/services/ThreatDetection"));
const SecurityTraining = lazyWithDelay(() => import("./pages/services/SecurityTraining"));
const AIGoogleReview = lazyWithDelay(() => import("./pages/services/AIGoogleReview"));
const GoogleAdsServices = lazyWithDelay(() => import("./pages/services/GoogleAdsServices"));
const MetaAdsServices = lazyWithDelay(() => import("./pages/services/MetaAdsServices"));
const GoogleAdsAnalyzer = lazyWithDelay(() => import("./pages/GoogleAdsAnalyzer"));
const Portfolio = lazyWithDelay(() => import("./pages/Portfolio"));
const PortfolioShowcase = lazyWithDelay(() => import("./pages/PortfolioShowcase"));
const PortfolioProjectPage = lazyWithDelay(() => import("./pages/PortfolioProjectPage"));
const Ecosystem = lazyWithDelay(() => import("./pages/Ecosystem"));
const Team = lazyWithDelay(() => import("./pages/Team"));
const TeamMember = lazyWithDelay(() => import("./pages/TeamMember"));
const InnovationDepartment = lazyWithDelay(() => import("./pages/InnovationDepartment"));
const Platform = lazyWithDelay(() => import("./pages/Platform"));
const Booking = lazyWithDelay(() => import("./pages/platform/Booking"));
const Ecommerce = lazyWithDelay(() => import("./pages/platform/Ecommerce"));
const Ordering = lazyWithDelay(() => import("./pages/platform/Ordering"));
const Delivery = lazyWithDelay(() => import("./pages/platform/Delivery"));
const AIAutomation = lazyWithDelay(() => import("./pages/platform/AIAutomation"));
const Media = lazyWithDelay(() => import("./pages/platform/Media"));
const Social = lazyWithDelay(() => import("./pages/platform/Social"));
const SocialAnalytics = lazyWithDelay(() => import("./pages/platform/Analytics"));
const SiteBuilder = lazyWithDelay(() => import("./pages/platform/SiteBuilder"));
const FormResponses = lazyWithDelay(() => import("./pages/FormResponses"));
const PublicForm = lazyWithDelay(() => import("./pages/PublicForm"));
const FormSettingsManager = lazyWithDelay(() => import("./pages/FormSettingsManager"));
const FormsManager = lazyWithDelay(() => import("./pages/FormsManager"));
const EcommerceAdmin = lazyWithDelay(() => import("./pages/EcommerceAdmin"));
const AdminPortfolio = lazyWithDelay(() => import("./pages/AdminPortfolio"));
const BookingAdmin = lazyWithDelay(() => import("./pages/BookingAdmin"));
const PublicBooking = lazyWithDelay(() => import("./pages/PublicBooking"));
const GuestBookingManage = lazyWithDelay(() => import("./pages/GuestBookingManage"));
const BookingSuccess = lazyWithDelay(() => import("./pages/BookingSuccess"));
const PrivacyPolicy = lazyWithDelay(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazyWithDelay(() => import("./pages/TermsOfService"));
const CookiePolicy = lazyWithDelay(() => import("./pages/CookiePolicy"));
const RefundPolicy = lazyWithDelay(() => import("./pages/RefundPolicy"));
const SEOAnalyzer = lazyWithDelay(() => import("./pages/SEOAnalyzer"));
const SecurityAnalyzerPage = lazyWithDelay(() => import("./pages/SecurityAnalyzerPage"));
const LeadManagement = lazyWithDelay(() => import("./pages/LeadManagement"));
const GoogleReviewAnalyzer = lazyWithDelay(() => import("./pages/GoogleReviewAnalyzer"));
const CustomerServices = lazyWithDelay(() => import("./pages/CustomerServices"));
const CustomerContracts = lazyWithDelay(() => import("./pages/CustomerContracts"));
const CustomerInvoices = lazyWithDelay(() => import("./pages/CustomerInvoices"));
const PaymentMethods = lazyWithDelay(() => import("./pages/PaymentMethods"));
const AuditHistory = lazyWithDelay(() => import("./pages/AuditHistory"));
const AnalysisHistory = lazyWithDelay(() => import("./pages/AnalysisHistory"));
const AdminServicesManager = lazyWithDelay(() => import("./pages/AdminServicesManager"));
const AdminInvoicesManager = lazyWithDelay(() => import("./pages/AdminInvoicesManager"));
const AdminContractsManager = lazyWithDelay(() => import("./pages/AdminContractsManager"));
const AdminPaymentMethodsManager = lazyWithDelay(() => import("./pages/AdminPaymentMethodsManager"));
const AdminOverview = lazyWithDelay(() => import("./pages/AdminOverview"));
const StaffDashboard = lazyWithDelay(() => import("./pages/StaffDashboard"));
const MarketerDashboard = lazyWithDelay(() => import("./pages/MarketerDashboard"));
const SalesDashboard = lazyWithDelay(() => import("./pages/SalesDashboard"));
const Leaderboard = lazyWithDelay(() => import("./pages/Leaderboard"));
const CustomerDashboard = lazyWithDelay(() => import("./pages/CustomerDashboard"));
const AdminHRDashboard = lazyWithDelay(() => import("./pages/AdminHRDashboard"));
const AdminProjects = lazyWithDelay(() => import("./pages/AdminProjects"));
const AdminDepartments = lazyWithDelay(() => import("./pages/AdminDepartments"));
const AdminMeetings = lazyWithDelay(() => import("./pages/AdminMeetings"));
const AdminStaffManagement = lazyWithDelay(() => import("./pages/AdminStaffManagement"));
const ProjectsManager = lazyWithDelay(() => import("./pages/ProjectsManager"));
const TasksManager = lazyWithDelay(() => import("./pages/TasksManager"));
const ClientsManager = lazyWithDelay(() => import("./pages/ClientsManager"));
const TimeTracking = lazyWithDelay(() => import("./pages/TimeTracking"));
const EstimatesManager = lazyWithDelay(() => import("./pages/EstimatesManager"));
const ExpensesManager = lazyWithDelay(() => import("./pages/ExpensesManager"));
const SupportTickets = lazyWithDelay(() => import("./pages/SupportTickets"));
const InternalMessages = lazyWithDelay(() => import("./pages/InternalMessages"));
const KnowledgeBase = lazyWithDelay(() => import("./pages/KnowledgeBase"));
const EventsCalendar = lazyWithDelay(() => import("./pages/EventsCalendar"));
const NotesManager = lazyWithDelay(() => import("./pages/NotesManager"));
const ProposalsManager = lazyWithDelay(() => import("./pages/ProposalsManager"));
const ProposalGenerator = lazyWithDelay(() => import("./pages/ProposalGenerator"));
const CreditNotesManager = lazyWithDelay(() => import("./pages/CreditNotesManager"));
const SubscriptionsManager = lazyWithDelay(() => import("./pages/SubscriptionsManager"));
const ReportsManager = lazyWithDelay(() => import("./pages/ReportsManager"));
const ContactsManager = lazyWithDelay(() => import("./pages/ContactsManager"));
const LeaveManagement = lazyWithDelay(() => import("./pages/LeaveManagement"));
const PerformanceReviews = lazyWithDelay(() => import("./pages/PerformanceReviews"));
const AnnouncementsManager = lazyWithDelay(() => import("./pages/AnnouncementsManager"));
const EmailClient = lazyWithDelay(() => import("./pages/EmailClient"));
const CouponsManager = lazyWithDelay(() => import("./pages/CouponsManager"));
const EmailTemplatesManager = lazyWithDelay(() => import("./pages/EmailTemplatesManager"));
const RemindersManager = lazyWithDelay(() => import("./pages/RemindersManager"));
const BlogCategoriesManager = lazyWithDelay(() => import("./pages/BlogCategoriesManager"));
const AttendanceManager = lazyWithDelay(() => import("./pages/AttendanceManager"));
const MilestonesManager = lazyWithDelay(() => import("./pages/MilestonesManager"));
const ClientDetail = lazyWithDelay(() => import("./pages/ClientDetail"));
const LeadDetail = lazyWithDelay(() => import("./pages/LeadDetail"));
const StaffDetail = lazyWithDelay(() => import("./pages/StaffDetail"));
const TasksKanban = lazyWithDelay(() => import("./pages/TasksKanban"));
const SalesFunnel = lazyWithDelay(() => import("./pages/SalesFunnel"));
const AIPhoneAgent = lazyWithDelay(() => import("./pages/AIPhoneAgent"));
const AIPhoneCommandCenter = lazyWithDelay(() => import("./pages/AIPhoneCommandCenter"));
const Restaurants = lazyWithDelay(() => import("./pages/Restaurants"));
const RestaurantDetail = lazyWithDelay(() => import("./pages/RestaurantDetail"));
const BookingConfirmation = lazyWithDelay(() => import("./pages/BookingConfirmation"));
const ReservationSuccess = lazyWithDelay(() => import("./pages/ReservationSuccess"));
const MyReservations = lazyWithDelay(() => import("./pages/MyReservations"));
const RestaurantOwnerDashboard = lazyWithDelay(() => import("./pages/RestaurantOwnerDashboard"));
const AdminRestaurantsDashboard = lazyWithDelay(() => import("./pages/AdminRestaurantsDashboard"));
const RestaurantHome = lazyWithDelay(() => import("./pages/RestaurantHome"));
const RewardsPage = lazyWithDelay(() => import("./pages/RewardsPage"));
const PrivateDiningPage = lazyWithDelay(() => import("./pages/PrivateDiningPage"));
const WaitlistPage = lazyWithDelay(() => import("./pages/WaitlistPage"));
const RestaurantSearch = lazyWithDelay(() => import("./pages/RestaurantSearch"));
const BookMeeting = lazyWithDelay(() => import("./pages/BookMeeting"));

// Mobile App Pages (OpenTable-style)
const AppHome = lazyWithDelay(() => import("./pages/app/AppHome"));
const AppSearch = lazyWithDelay(() => import("./pages/app/AppSearch"));
const AppRewards = lazyWithDelay(() => import("./pages/app/AppRewards"));
const AppReservations = lazyWithDelay(() => import("./pages/app/AppReservations"));
const AppUpdates = lazyWithDelay(() => import("./pages/app/AppUpdates"));
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { RoleBasedRoute } from "./components/RoleBasedRoute";
import { AdminRoute } from "./components/AdminRoute";

// 301-equivalent client-side redirect for legacy /p/:slug → /blog/:slug
// (consolidates SEO authority on the canonical /blog/ URL)
function LegacyBlogRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
}


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  },
});

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  
  const isEmailClient = location.pathname.includes('/email-client');

  return (
    <div className="min-h-screen w-full">
      <ImpersonationBanner />
      <ResponsiveSidebar />
      <main
        className={`lg:ml-56 pt-14 bg-background ${isEmailClient ? 'h-[calc(100vh-3.5rem)] overflow-hidden flex flex-col' : 'min-h-[calc(100vh-3.5rem)]'}`}
      >
        <div key={location.pathname} className={`animate-fade-in ${isEmailClient ? 'flex-1 overflow-hidden h-full' : ''}`}>
          {children}
        </div>
      </main>
    </div>
  );
};

// Single loading state - removed duplicate RouteLoader to prevent double loading

const App = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <AuthProvider>
              <PlatformProvider>
                <QuoteModalProvider>
                  <CalendlyProvider>
                    <AppErrorBoundary>
                    <Suspense fallback={<PitechPageLoader text="Loading..." />}>
                      <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/our-services" element={<Services />} />
              <Route path="/our-services/" element={<Services />} />
              <Route path="/service" element={<Navigate to="/our-services" replace />} />
              <Route path="/service/" element={<Navigate to="/our-services" replace />} />
              <Route path="/services/custom-software" element={<CustomSoftware />} />
              <Route path="/services/mobile-app-development" element={<MobileAppDevelopment />} />
              <Route path="/services/web-design" element={<WebAppDevelopment />} />
              <Route path="/services/cloud-solutions" element={<CloudSolutions />} />
              <Route path="/services/enterprise-software" element={<EnterpriseSoftware />} />
              <Route path="/services/software-consulting" element={<SoftwareConsulting />} />
              <Route path="/services/domain-services" element={<DomainServices />} />
              <Route path="/services/seo" element={<SEO />} />
              <Route path="/services/ppc-advertising" element={<PPCAdvertising />} />
              <Route path="/services/email-marketing" element={<EmailMarketing />} />
              <Route path="/services/social-media-marketing" element={<SocialMediaMarketing />} />
              <Route path="/services/content-marketing" element={<ContentMarketing />} />
              <Route path="/services/digital-marketing" element={<DigitalMarketing />} />
              <Route path="/services/analytics-reporting" element={<AnalyticsReporting />} />
              <Route path="/services/creative-studio" element={<CreativeStudio />} />
              <Route path="/services/vulnerability-assessment" element={<VulnerabilityAssessment />} />
              <Route path="/services/security-consulting" element={<SecurityConsulting />} />
              <Route path="/services/data-protection" element={<DataProtection />} />
              <Route path="/services/network-security" element={<NetworkSecurity />} />
              <Route path="/services/threat-detection" element={<ThreatDetection />} />
              <Route path="/services/security-training" element={<SecurityTraining />} />
              <Route path="/services/ai-google-review" element={<AIGoogleReview />} />
              <Route path="/services/canada" element={<ServicesCanada />} />
              <Route path="/services/canada/" element={<ServicesCanada />} />
              <Route path="/services/near-me" element={<ServicesNearMe />} />
              <Route path="/services/near-me/" element={<ServicesNearMe />} />
              {/* Province and City SEO Pages */}
              <Route path="/services/canada/:provinceSlug" element={<ProvincePage />} />
              <Route path="/services/canada/:provinceSlug/:citySlug" element={<CityPage />} />
              <Route path="/google-ads-services" element={<GoogleAdsServices />} />
              <Route path="/google-ads-services/" element={<GoogleAdsServices />} />
              <Route path="/meta-ads-services" element={<MetaAdsServices />} />
              <Route path="/meta-ads-services/" element={<MetaAdsServices />} />
              <Route path="/portfolio" element={<Navigate to="/our-work" replace />} />
              <Route path="/our-work" element={<PortfolioShowcase />} />
              <Route path="/our-work/:projectSlug" element={<PortfolioProjectPage />} />
              <Route path="/ecosystem" element={<Ecosystem />} />
              <Route path="/our-team" element={<Team />} />
              <Route path="/our-team/" element={<Team />} />
              <Route path="/innovation" element={<InnovationDepartment />} />
              <Route path="/innovation/" element={<InnovationDepartment />} />
              <Route path="/inovation" element={<Navigate to="/innovation" replace />} />
              <Route path="/inovation/" element={<Navigate to="/innovation" replace />} />
              <Route path="/shayan-erfanian" element={<TeamMember />} />
              <Route path="/shayan-erfanian/" element={<TeamMember />} />
              <Route path="/reza-zahedi" element={<TeamMember />} />
              <Route path="/reza-zahedi/" element={<TeamMember />} />
              <Route path="/mohammad-ali-seifi-kashani" element={<TeamMember />} />
              <Route path="/mohammad-ali-seifi-kashani/" element={<TeamMember />} />
              <Route path="/sara-tanabbod" element={<TeamMember />} />
              <Route path="/sara-tanabbod/" element={<TeamMember />} />
              <Route path="/iliya-seifi-kashani" element={<TeamMember />} />
              <Route path="/iliya-seifi-kashani/" element={<TeamMember />} />
              <Route path="/amir-azarbad" element={<TeamMember />} />
              <Route path="/amir-azarbad/" element={<TeamMember />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/about-us" element={<About />} />
              <Route path="/about-us/" element={<About />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/contact-us/" element={<Contact />} />
              <Route path="/contact" element={<Navigate to="/contact-us" replace />} />
              <Route path="/contact/" element={<Navigate to="/contact-us" replace />} />
              <Route path="/quote/thank-you" element={<QuoteThankYou />} />
              <Route path="/quote" element={<GetAQuote />} />
              <Route path="/get-a-quote" element={<Navigate to="/quote" replace />} />
              <Route path="/get-a-quote/" element={<Navigate to="/quote" replace />} />
              <Route path="/services" element={<Navigate to="/our-services" replace />} />
              <Route path="/services/" element={<Navigate to="/our-services" replace />} />
              <Route path="/about" element={<Navigate to="/about-us" replace />} />
              <Route path="/about/" element={<Navigate to="/about-us" replace />} />
              <Route path="/team" element={<Navigate to="/our-team" replace />} />
              <Route path="/team/" element={<Navigate to="/our-team" replace />} />
              <Route path="/platform" element={<Platform />} />
              <Route path="/platform/site-builder" element={<SiteBuilder />} />
              <Route path="/platform/booking" element={<Booking />} />
              <Route path="/platform/ecommerce" element={<Ecommerce />} />
              <Route path="/platform/ordering" element={<Ordering />} />
              <Route path="/platform/delivery" element={<Delivery />} />
              <Route path="/platform/ai-automation" element={<AIAutomation />} />
              <Route path="/platform/media" element={<Media />} />
              <Route path="/platform/social" element={<Social />} />
              <Route path="/platform/analytics" element={<SocialAnalytics />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/enhanced-auth" element={<EnhancedAuth />} />
              <Route path="/s/:code" element={<ShortLink />} />
              <Route path="/menu/:id" element={<PublicMenu />} />
              <Route path="/reservation/:id" element={<PublicReservation />} />
              <Route path="/appointment/:id" element={<PublicAppointment />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/tag/:tag" element={<BlogTagPage />} />
              <Route path="/blog/category/:slug" element={<BlogCategoryPage />} />
              <Route path="/blog/:slug" element={<PublicBlog />} />
              
              {/* Restaurant Reservation Routes */}
              <Route path="/restaurants" element={<Restaurants />} />
              <Route path="/restaurants/:id" element={<RestaurantDetail />} />
              <Route path="/restaurants/:id/book" element={
                <ProtectedRoute>
                  <BookingConfirmation />
                </ProtectedRoute>
              } />
              <Route path="/reservations/success" element={<ReservationSuccess />} />
              <Route path="/my-reservations" element={
                <ProtectedRoute>
                  <MyReservations />
                </ProtectedRoute>
              } />
              
              {/* Restaurant Owner & Admin Dashboards */}
              <Route path="/restaurant-dashboard" element={
                <ProtectedRoute>
                  <RestaurantOwnerDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/restaurants" element={
                <RoleBasedRoute allowedRoles={['admin', 'owner']}>
                  <AdminRestaurantsDashboard />
                </RoleBasedRoute>
              } />
              
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
              <Route path="/cookie-policy" element={<CookiePolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/seo-analyzer" element={<SEOAnalyzer />} />
              <Route path="/seo-report/:slug" element={<SEOAnalyzer />} />
              <Route path="/tools/security-analyzer" element={<SecurityAnalyzerPage />} />
              <Route path="/security-analyzer" element={<SecurityAnalyzerPage />} />
              <Route path="/google-review-analyzer" element={<GoogleReviewAnalyzer />} />
              <Route path="/tools/google-review-analyzer" element={<GoogleReviewAnalyzer />} />
              <Route path="/google-ads-analyzer" element={<GoogleAdsAnalyzer />} />
              <Route path="/tools/google-ads-analyzer" element={<GoogleAdsAnalyzer />} />
              <Route path="/gift-card-activation" element={<GiftCardActivation />} />
              <Route path="/gift-cards" element={<GiftCards />} />
              <Route path="/book" element={<BookMeeting />} />
              <Route path="/book-meeting" element={<BookMeeting />} />
              
              {/* Dashboard Routes with Sidebar */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <DashboardLayout><Dashboard /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/qr-codes" element={
                <ProtectedRoute>
                  <DashboardLayout><QRCodes /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/qr-codes/create" element={
                <ProtectedRoute>
                  <DashboardLayout><CreateQR /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/analytics" element={
                <ProtectedRoute>
                  <DashboardLayout><Analytics /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/analytics/:id" element={
                <ProtectedRoute>
                  <DashboardLayout><AnalyticsDetailed /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/analytics-detailed" element={
                <ProtectedRoute>
                  <DashboardLayout><AnalyticsDetailed /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/settings" element={
                <ProtectedRoute>
                  <DashboardLayout><Settings /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/admin" element={
                <AdminRoute>
                  <DashboardLayout><AdminOverview /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/invoices" element={
                <ProtectedRoute>
                  <DashboardLayout><Invoices /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/notifications" element={
                <ProtectedRoute>
                  <DashboardLayout><Notifications /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/menu-builder" element={
                <ProtectedRoute>
                  <DashboardLayout><MenuBuilder /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/menu-builder/create" element={
                <ProtectedRoute>
                  <DashboardLayout><CreateMenu /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/menu-builder/:id" element={
                <ProtectedRoute>
                  <DashboardLayout><MenuBuilderComplete /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/menu-builder/:id/edit" element={
                <ProtectedRoute>
                  <DashboardLayout><EditMenuComplete /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/category/:id/edit" element={
                <ProtectedRoute>
                  <DashboardLayout><EditCategory /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/qr-designer" element={
                <ProtectedRoute>
                  <DashboardLayout><QRDesigner /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/menu-builder/:id/preview" element={
                <ProtectedRoute>
                  <PublicMenu />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/affiliate" element={
                <ProtectedRoute>
                  <DashboardLayout><AffiliatePortal /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/affiliate/settings" element={
                <ProtectedRoute>
                  <DashboardLayout><AffiliateSettings /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/marketing" element={
                <AdminRoute>
                  <DashboardLayout><MarketingCampaigns /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/google-marketing" element={
                <AdminRoute>
                  <DashboardLayout><GoogleMarketing /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/campaigns/create" element={
                <AdminRoute>
                  <DashboardLayout><CreateCampaign /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/session-replays" element={
                <AdminRoute>
                  <DashboardLayout><SessionReplays /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/blog" element={
                <AdminRoute>
                  <DashboardLayout><BlogManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/blog/create" element={
                <ProtectedRoute>
                  <DashboardLayout><CreateBlogComplete /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/edit/:id" element={
                <ProtectedRoute>
                  <DashboardLayout><EditBlog /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/analytics/:id" element={
                <ProtectedRoute>
                  <DashboardLayout><BlogPostAnalytics /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog/content-strategy" element={
                <AdminRoute>
                  <DashboardLayout><ContentStrategyPage /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/wordpress-import" element={
                <ProtectedRoute>
                  <DashboardLayout><SimpleWordPressImport /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/roles" element={
                <AdminRoute>
                  <DashboardLayout><RoleManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/files" element={
                <ProtectedRoute>
                  <DashboardLayout><FileManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/site-config" element={
                <AdminRoute>
                  <DashboardLayout><SiteConfiguration /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/page-seo" element={
                <AdminRoute>
                  <DashboardLayout><AdminPageSeo /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/tenants" element={
                <AdminRoute>
                  <DashboardLayout><TenantManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/subscription-plans" element={
                <AdminRoute>
                  <DashboardLayout><SubscriptionPlansAdmin /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/saas-features" element={
                <AdminRoute>
                  <DashboardLayout><SaaSFeaturesDashboard /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/security" element={
                <AdminRoute>
                  <DashboardLayout><Security /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/api-gateway" element={
                <AdminRoute>
                  <DashboardLayout><APIGatewayDashboard /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/mlm-programs" element={
                <AdminRoute>
                  <DashboardLayout><MLMPrograms /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/gift-cards" element={
                <ProtectedRoute>
                  <DashboardLayout><GiftCardAdmin /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/test-payment" element={
                <ProtectedRoute>
                  <DashboardLayout><TestPayment /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/join" element={
                <ProtectedRoute>
                  <DashboardLayout><JoinProgram /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/partner-portal" element={
                <ProtectedRoute>
                  <DashboardLayout><PartnerPortal /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/wallet" element={
                <ProtectedRoute>
                  <DashboardLayout><UserWallet /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/my-services" element={
                <ProtectedRoute>
                  <DashboardLayout><CustomerServices /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/my-contracts" element={
                <ProtectedRoute>
                  <DashboardLayout><CustomerContracts /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/my-invoices" element={
                <ProtectedRoute>
                  <DashboardLayout><CustomerInvoices /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/payment-methods" element={
                <ProtectedRoute>
                  <DashboardLayout><PaymentMethods /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/audit-history" element={
                <ProtectedRoute>
                  <DashboardLayout><AuditHistory /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/analysis-history" element={
                <ProtectedRoute>
                  <DashboardLayout><AnalysisHistory /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/rewards" element={
                <ProtectedRoute>
                  <DashboardLayout><RewardsPrograms /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/payouts" element={
                <ProtectedRoute>
                  <DashboardLayout><WalletPayouts /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/wallet-admin" element={
                <AdminRoute>
                  <DashboardLayout><WalletAdmin /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/user-management" element={
                <AdminRoute>
                  <DashboardLayout><AdminUserManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-services" element={
                <AdminRoute>
                  <DashboardLayout><AdminServicesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-invoices" element={
                <AdminRoute>
                  <DashboardLayout><AdminInvoicesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-contracts" element={
                <AdminRoute>
                  <DashboardLayout><AdminContractsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-payments" element={
                <AdminRoute>
                  <DashboardLayout><AdminPaymentMethodsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-appointments" element={
                <AdminRoute>
                  <DashboardLayout><AdminAppointments /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-reservations" element={
                <AdminRoute>
                  <DashboardLayout><AdminReservations /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/admin-floorplan" element={
                <AdminRoute>
                  <DashboardLayout><AdminFloorPlan /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/pages" element={
                <AdminRoute>
                  <DashboardLayout><PageManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/my-pages" element={
                <AdminRoute>
                  <DashboardLayout><PageManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/page-builder/:pageId?" element={
                <AdminRoute>
                  <PageBuilder />
                </AdminRoute>
              } />
              <Route path="/dashboard/api-settings" element={
                <ProtectedRoute>
                  <DashboardLayout><APISettings /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/complete-wallet" element={
                <ProtectedRoute>
                  <DashboardLayout><CompleteWallet /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/form-builder" element={
                <ProtectedRoute>
                  <DashboardLayout><FormBuilder /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/form-settings" element={
                <AdminRoute>
                  <DashboardLayout><FormSettingsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/forms" element={
                <ProtectedRoute>
                  <DashboardLayout><FormsManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              {/* Legacy /p/:slug URLs redirect to canonical /blog/:slug to consolidate SEO authority */}
              <Route path="/p/:slug" element={<LegacyBlogRedirect />} />
              {/* Legacy WordPress /tag/* URLs redirect to /blog */}
              <Route path="/tag/*" element={<Navigate to="/blog" replace />} />
              <Route path="/category/*" element={<Navigate to="/blog" replace />} />

              {/* E-commerce Routes */}
              <Route path="/store" element={<Store />} />
              <Route path="/product/:slug" element={<ProductDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/dashboard/portfolio" element={
                <AdminRoute>
                  <DashboardLayout><AdminPortfolio /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/ecommerce" element={
                <AdminRoute>
                  <DashboardLayout><EcommerceAdmin /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/admin-products" element={
                <AdminRoute>
                  <DashboardLayout><AdminProducts /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/admin-orders" element={
                <AdminRoute>
                  <DashboardLayout><AdminOrders /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/admin-categories" element={
                <AdminRoute>
                  <DashboardLayout><AdminCategories /></DashboardLayout>
                </AdminRoute>
              } />
              
              <Route path="/dashboard/leads" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner', 'sales_marketing', 'marketer']}>
                  <DashboardLayout><LeadManagement /></DashboardLayout>
              </RoleBasedRoute>
              } />
              <Route path="/dashboard/sales-funnel" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner', 'sales_marketing', 'marketer']}>
                  <DashboardLayout><SalesFunnel /></DashboardLayout>
                </RoleBasedRoute>
              } />
              
              <Route path="/admin-blog-import" element={<AdminBlogImport />} />
              <Route path="/admin-newsletter" element={<AdminNewsletter />} />
              
              {/* Smart Booking Routes */}
              <Route path="/book/:userId" element={<PublicBooking />} />
              <Route path="/book/:userId/:serviceId" element={<PublicBooking />} />
              <Route path="/booking/manage/:appointmentId" element={<GuestBookingManage />} />
              <Route path="/booking-success" element={<BookingSuccess />} />
              <Route path="/dashboard/booking" element={
                <ProtectedRoute>
                  <DashboardLayout><BookingAdmin /></DashboardLayout>
                </ProtectedRoute>
              } />
              
              {/* AI Phone Agent */}
              <Route path="/dashboard/ai-phone-agent" element={
                <ProtectedRoute>
                  <AIPhoneAgent />
                </ProtectedRoute>
              } />
              <Route path="/dashboard/ai-phone-command-center" element={
                <AdminRoute>
                  <DashboardLayout><AIPhoneCommandCenter /></DashboardLayout>
                </AdminRoute>
              } />
              
              {/* Role-Based Dashboards */}
              <Route path="/staff" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner']}>
                  <StaffDashboard />
                </RoleBasedRoute>
              } />
              <Route path="/marketer" element={
                <RoleBasedRoute allowedRoles={['marketer', 'sales_marketing', 'admin', 'owner']}>
                  <MarketerDashboard />
                </RoleBasedRoute>
              } />
              <Route path="/dashboard/leaderboard" element={
                <RoleBasedRoute allowedRoles={['marketer', 'sales_marketing', 'admin', 'owner']}>
                  <Leaderboard />
                </RoleBasedRoute>
              } />
              <Route path="/sales" element={
                <RoleBasedRoute allowedRoles={['sales_marketing', 'marketer', 'admin', 'owner']}>
                  <SalesDashboard />
                </RoleBasedRoute>
              } />
              <Route path="/customer" element={
                <RoleBasedRoute allowedRoles={['customer', 'user', 'admin', 'owner']}>
                  <CustomerDashboard />
                </RoleBasedRoute>
              } />
              
              {/* HR Admin Routes */}
              <Route path="/dashboard/hr" element={
                <AdminRoute>
                  <DashboardLayout><AdminHRDashboard /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/projects" element={
                <AdminRoute>
                  <DashboardLayout><AdminProjects /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/departments" element={
                <AdminRoute>
                  <DashboardLayout><AdminDepartments /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/meetings" element={
                <AdminRoute>
                  <DashboardLayout><AdminMeetings /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/staff" element={
                <AdminRoute>
                  <DashboardLayout><AdminStaffManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/tasks" element={
                <ProtectedRoute>
                  <DashboardLayout><TasksManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/clients" element={
                <ProtectedRoute>
                  <DashboardLayout><ClientsManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/clients/:id" element={
                <ProtectedRoute>
                  <DashboardLayout><ClientDetail /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/leads/:id" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner', 'sales_marketing', 'marketer']}>
                  <DashboardLayout><LeadDetail /></DashboardLayout>
                </RoleBasedRoute>
              } />
              <Route path="/dashboard/staff/:id" element={
                <AdminRoute>
                  <DashboardLayout><StaffDetail /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/tasks-kanban" element={
                <ProtectedRoute>
                  <DashboardLayout><TasksKanban /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/time-tracking" element={
                <ProtectedRoute>
                  <DashboardLayout><TimeTracking /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/crm-projects" element={
                <ProtectedRoute>
                  <DashboardLayout><ProjectsManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/estimates" element={
                <AdminRoute>
                  <DashboardLayout><EstimatesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/expenses" element={
                <AdminRoute>
                  <DashboardLayout><ExpensesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/support-tickets" element={
                <AdminRoute>
                  <DashboardLayout><SupportTickets /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/messages" element={
                <ProtectedRoute>
                  <DashboardLayout><InternalMessages /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/knowledge-base" element={
                <ProtectedRoute>
                  <DashboardLayout><KnowledgeBase /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/events" element={
                <ProtectedRoute>
                  <DashboardLayout><EventsCalendar /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/notes" element={
                <ProtectedRoute>
                  <DashboardLayout><NotesManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/proposals" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner', 'sales_marketing', 'marketer']}>
                  <DashboardLayout><ProposalsManager /></DashboardLayout>
                </RoleBasedRoute>
              } />
              <Route path="/dashboard/proposal-generator" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner', 'sales_marketing', 'marketer']}>
                  <DashboardLayout><ProposalGenerator /></DashboardLayout>
                </RoleBasedRoute>
              } />
              <Route path="/dashboard/credit-notes" element={
                <AdminRoute>
                  <DashboardLayout><CreditNotesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/subscriptions" element={
                <AdminRoute>
                  <DashboardLayout><SubscriptionsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/ai-editors" element={
                <RoleBasedRoute allowedRoles={['staff', 'admin', 'owner', 'sales_marketing', 'marketer']}>
                  <AIEditorsAdmin />
                </RoleBasedRoute>
              } />
              <Route path="/dashboard/user-activity" element={
                <AdminRoute>
                  <UserActivityTracking />
                </AdminRoute>
              } />
              <Route path="/dashboard/reports" element={
                <AdminRoute>
                  <DashboardLayout><ReportsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/contacts" element={
                <AdminRoute>
                  <DashboardLayout><ContactsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/leave-management" element={
                <AdminRoute>
                  <DashboardLayout><LeaveManagement /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/performance-reviews" element={
                <AdminRoute>
                  <DashboardLayout><PerformanceReviews /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/announcements" element={
                <AdminRoute>
                  <DashboardLayout><AnnouncementsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/coupons" element={
                <AdminRoute>
                  <DashboardLayout><CouponsManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/email-client" element={
                <ProtectedRoute>
                  <EmailClient />
                </ProtectedRoute>
              } />

              <Route path="/dashboard/email-templates" element={
                <AdminRoute>
                  <DashboardLayout><EmailTemplatesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/reminders" element={
                <ProtectedRoute>
                  <DashboardLayout><RemindersManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/blog-categories" element={
                <AdminRoute>
                  <DashboardLayout><BlogCategoriesManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/attendance" element={
                <AdminRoute>
                  <DashboardLayout><AttendanceManager /></DashboardLayout>
                </AdminRoute>
              } />
              <Route path="/dashboard/milestones" element={
                <ProtectedRoute>
                  <DashboardLayout><MilestonesManager /></DashboardLayout>
                </ProtectedRoute>
              } />
              <Route path="/dashboard/form-responses/:formId" element={
                <ProtectedRoute>
                  <DashboardLayout><FormResponses /></DashboardLayout>
                </ProtectedRoute>
              } />
              
              {/* Restaurant Booking System Routes */}
              <Route path="/restaurants/search" element={<RestaurantSearch />} />
              <Route path="/restaurants/home" element={<RestaurantHome />} />
              <Route path="/rewards" element={<RewardsPage />} />
              <Route path="/private-dining" element={<PrivateDiningPage />} />
              <Route path="/my-waitlist" element={<WaitlistPage />} />
              
              {/* Mobile App Routes (OpenTable-style) */}
              <Route path="/app" element={<AppHome />} />
              <Route path="/app/search" element={<AppSearch />} />
              <Route path="/app/rewards" element={<AppRewards />} />
              <Route path="/app/reservations" element={<AppReservations />} />
              <Route path="/app/updates" element={<AppUpdates />} />
              
              <Route path="/subscription-expired" element={<SubscriptionExpired />} />
              <Route path="*" element={<NotFound />} />
              </Routes>
                    </Suspense>
                  </AppErrorBoundary>
                </CalendlyProvider>
              </QuoteModalProvider>
            </PlatformProvider>
          </AuthProvider>
          
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
